import { Link } from 'react-router-dom';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqs } from '@/mocks/faqs';

interface CategoryCardProps {
  slug: string;
  name: string;
  description: string;
  icon: string;
  count: number;
  image: string;
  regionLabel: string;
}

function CategoryCard({ slug, name, description, icon, count, image, regionLabel }: CategoryCardProps) {
  return (
    <Link
      to={`/category/${slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300"
    >
      <div className="relative w-full h-48 overflow-hidden">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        <div className="absolute bottom-3 left-3 right-3">
          <span className="text-xs text-background-50 bg-primary-500/90 px-2.5 py-1 rounded-full whitespace-nowrap">
            {count} 篇文章
          </span>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2.5 mb-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
            <i className={`${icon} text-base`}></i>
          </span>
          <h3 className="font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200">
            {name}
          </h3>
        </div>
        <p className="text-sm text-foreground-600 leading-relaxed mb-2 line-clamp-2">{description}</p>
        <p className="text-xs text-foreground-400">{regionLabel}</p>
      </div>
    </Link>
  );
}

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  categoryName: string;
  date: string;
  readTime: string;
  image: string;
}

function ArticleCard({ slug, title, excerpt, categoryName, date, readTime, image }: ArticleCardProps) {
  return (
    <Link
      to={`/article/${slug}`}
      className="group flex flex-col sm:flex-row gap-5 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-300 overflow-hidden"
    >
      <div className="relative w-full sm:w-56 h-44 sm:h-auto flex-shrink-0 overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 py-4 pr-5 pl-5 sm:pl-0 flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full whitespace-nowrap">
            {categoryName}
          </span>
          <span className="text-xs text-foreground-400">{date}</span>
          <span className="text-xs text-foreground-400">{readTime}</span>
        </div>
        <h3 className="font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 mb-1.5 line-clamp-2">
          {title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{excerpt}</p>
      </div>
    </Link>
  );
}

export default function Home() {
  const featuredArticles = articles.slice(0, 4);

  return (
    <div className="min-h-screen">
      <SchemaOrg type="WebSite" data={{
        name: '日本区域指南',
        url: 'https://japan-zone-guide.readdy.ai/',
        description: '全面解析日本一区二区的区域划分概念、地理范围与实用信息。',
      }} />
      <SchemaOrg type="FAQPage" data={{
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }} />
      {/* Hero */}
      <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20stylized%20map%20of%20Japan%20with%20warm%20golden%20and%20amber%20color%20gradient%2C%20geometric%20minimalist%20design%2C%20soft%20artistic%20illustration%2C%20clean%20modern%20composition%2C%20elegant%20editorial%20style%2C%20warm%20atmospheric%20tones%2C%20no%20text%20no%20labels&width=1800&height=1000&seq=hero-japan-map&orientation=landscape"
          alt="日本区域指南"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative z-10 w-full text-center px-4 md:px-6">
          <h1 className="font-heading text-2xl md:text-5xl font-bold text-background-50 mb-4 leading-tight">
            日本一区二区完整指南
          </h1>
          <p className="text-sm md:text-lg text-background-200 max-w-2xl mx-auto mb-8 leading-relaxed">
            全面解析日本一区二区的区域划分概念、地理范围与实用信息。为您提供最清晰的区域说明、特色对比与出行参考，助您快速掌握核心知识。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/zone-one"
              className="px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              探索一区
            </Link>
            <Link
              to="/category/zone-two"
              className="px-6 py-3 bg-background-50/15 text-background-50 backdrop-blur-sm rounded-md font-medium text-sm hover:bg-background-50/25 border border-background-50/20 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              探索二区
            </Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-3">
              区域分类导航
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              选择您感兴趣的区域分类，深入了解日本一区二区的方方面面
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {categories.map((cat) => (
              <CategoryCard key={cat.slug} {...cat} />
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-10 md:mb-14">
            <div>
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-2">
                最新文章
              </h2>
              <p className="text-sm text-foreground-600">精选深度内容，助您全面了解日本区域信息</p>
            </div>
            <Link
              to="/category/zone-one"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap flex items-center gap-1"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="flex flex-col gap-5">
            {featuredArticles.map((article) => (
              <ArticleCard
                key={article.slug}
                slug={article.slug}
                title={article.title}
                excerpt={article.excerpt}
                categoryName={article.categoryName}
                date={article.date}
                readTime={article.readTime}
                image={article.image}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-600">关于日本一区二区，您可能想了解的</p>
          </div>
          <div className="space-y-3">
            {[
              { q: '日本一区具体包括哪些地方？', a: '日本一区通常指以东京为核心的关东地区主要城市圈，包括东京都（23区及多摩地区）、神奈川县（横滨市、川崎市等）、埼玉县、千叶县以及茨城县南部部分地区。' },
              { q: '日本二区的核心城市有哪些？', a: '日本二区以关西地区为核心，主要包括大阪府（大阪市）、京都府（京都市）、兵库县（神户市）、奈良县（奈良市）、滋贺县（大津市）以及和歌山县部分地区。' },
              { q: '一区和二区在气候上有什么区别？', a: '一区（关东）夏季炎热潮湿，冬季干燥寒冷，偶尔降雪。二区（关西）夏季同样炎热，但冬季相对温和。两区域在6月至7月均会经历梅雨季。一区太平洋沿岸冬季日照充足；京都因盆地地形冬季较为寒冷。' },
              { q: '从一区到二区最方便的交通方式是什么？', a: '从东京（一区）到大阪（二区），东海道新干线是最佳选择，最快约2小时30分钟，班次密集（白天每3-5分钟一班）。预算有限可选择夜行巴士（约8-9小时）或国内航班。' },
              { q: '生活在一区和二区的成本差距有多大？', a: '一区（东京）单身月均支出约15-20万日元（不含租金），二区（大阪）约12-16万日元。房租差距最大——东京中心1LDK月租约15-25万日元，大阪中心约8-15万日元，综合成本约低25%-35%。' },
              { q: '一区和二区哪个更适合留学？', a: '一区拥有东京大学、早稻田大学等顶尖学府，适合追求学术资源最大化和国际视野；二区拥有京都大学、大阪大学等，生活成本更低，文化氛围更温和。预算有限的学生，二区性价比更高。' },
            ].map((faq, i) => (
              <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-sm text-foreground-900 hover:text-primary-600 transition-colors duration-200">
                  <span>{faq.q}</span>
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-3">
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </span>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-4">
            准备探索日本一区二区了吗？
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-xl mx-auto">
            无论您是旅行、留学还是工作，我们为您准备了最全面的区域指南，助您做出最适合的选择。
          </p>
          <Link
            to="/category/comparison"
            className="inline-flex px-6 py-3 bg-accent-500 text-background-50 dark:text-foreground-950 rounded-md font-medium text-sm hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            查看区域对比
          </Link>
        </div>
      </section>
    </div>
  );
}